import type { AnalyzePrintingsRow, AnalyzePrintingsEvent, FailureBucket } from './types';

/**
 * Lower-cased `event.error` text. Mirrors legacy hardware-digester
 * (analyzePrintings.js `isFeederError`) which only inspects `event.error`,
 * not `rawContent`. The DB-level `Event.error` column is the canonical
 * place where firmware error strings land; `rawContent` is a JSON blob
 * that should not be string-matched (false-positive risk).
 */
const errorText = (e: AnalyzePrintingsEvent): string => (e.error ?? '').toLowerCase();

const matchesFilamentDeviation = (e: AnalyzePrintingsEvent): boolean => {
  if (e.eventType?.name !== 'print-error') return false;
  const t = errorText(e);
  return t.includes('feeder') && (t.includes('expected') || t.includes('deviation'));
};

const matchesLoadCell = (e: AnalyzePrintingsEvent): boolean => {
  if (e.eventType?.name !== 'print-error') return false;
  const t = errorText(e);
  return t.includes('feeder') && t.includes('load cell');
};

const matchesManualStop = (e: AnalyzePrintingsEvent): boolean =>
  e.eventType?.name === 'print-manual-stop';

const matchesFilamentRunout = (e: AnalyzePrintingsEvent): boolean => {
  if (e.eventType?.name !== 'print-error') return false;
  return errorText(e).includes('filamentrunout');
};

/**
 * Bucket a failed printing into one of the six legacy categories. Mirrors
 * the precedence chain in legacy `analyzePrintings.js` (lines 438-477):
 *
 *   1. Filament Deviation — has filament-deviation events AND no other category.
 *   2. Load Cell        — has load-cell events AND no other category.
 *   3. Manual Stop      — has manual-stop events AND no other category.
 *   4. Filament Runout  — has runout events AND no filament-deviation AND no
 *                          load-cell. (Note: legacy intentionally allows
 *                          `manualStop + filamentRunout` to land here, NOT in
 *                          Several Errors. Replicated faithfully.)
 *   5. Unfinished       — none of the above.
 *   6. Several Errors   — multiple categories that did not satisfy any of the
 *                          single-category branches above.
 */
export const bucketFailure = (row: AnalyzePrintingsRow): FailureBucket => {
  const filament = row.events.some(matchesFilamentDeviation);
  const loadCell = row.events.some(matchesLoadCell);
  const manualStop = row.events.some(matchesManualStop);
  const runout = row.events.some(matchesFilamentRunout);

  if (filament && !(loadCell || manualStop || runout)) return 'Filament Deviation Error';
  if (loadCell && !(filament || manualStop || runout)) return 'Load Cell Error';
  if (manualStop && !(filament || loadCell || runout)) return 'Manual Stop';
  if (runout && !(filament || loadCell)) return 'Filament Runout';
  if (!filament && !loadCell && !manualStop && !runout) return 'Unfinished Printings';
  return 'Several Errors';
};
